import { useNavigate, useParams, useLocation } from "react-router-dom";

import toast from "react-hot-toast";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import {
  axiosWithToast,
  HtmlTextFormatter,
  ItemStatus,
} from "../../../components/Utils";
import { PMField } from "../../../components/utils/PMField";
import { QuillEditor } from "../../../components/utils/quill-editor";
import { FormikSaveBar } from "../../../components/utils/FormikSaveBar";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import {
  useUserHasAccess,
  useWorkOrderPermissions,
  useProjectWorkOrdersAccess,
  useCompanyHasModule,
} from "../../../routes/roleOnly";
import { useSelector, useDispatch } from "react-redux";

import { getHoursAsync } from "../../../slices/hour";

import { PMNotice } from "../../../components/utils/notifications";
import { DocumentCenter } from "../../../components/dashboard/documents/document-center";
import {
  CheckBadgeIcon,
  TrashIcon,
  PrinterIcon,
  ArrowLeftIcon,
  CubeIcon,
  ClockIcon,
  PlusIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { PMDialog } from "../../../components/utils/PMDialog";
import { PMButton } from "../../../components/utils/PMButton";
import PMSwitch from "../../../components/utils/PMSwitch";
import ProConfirmation from "../../../components/utils/ProConfirmation";
import {
  createWorkOrder,
  deleteWorkOrder,
  fetchWorkOrder,
  updateWorkOrder,
} from "../../../api/work-order";
import { updateDeviation } from "../../../api/quality/deviation";
import { ProjectSelector } from "../../../components/utils/moduleSelectors/projectSelector";
import { DeliveryAddressSelector } from "../../../components/utils/moduleSelectors/deliveryAddressSelector";
import { CustomerSelector } from "../../../components/utils/moduleSelectors/customerSelector";
import ParticipantsSection from "../../../components/dashboard/projects/participants";
import { WorkOrderAccessesSection } from "../../../components/dashboard/workOrders/WorkOrderAccessesSection";
import { WorkOrderPrintDialog } from "../../../components/dashboard/workOrders/work-order-print-dialog";
import { WorkOrderEventsSection } from "../../../components/dashboard/workOrders/work-order-events-section";
import { workOrderParticipantsRole } from "../../../components/dashboard/workOrders/utils";
import { ModernProjectActivitySelectorField } from "../../../components/utils/moduleSelectors/ModernProjectActivitySelectorField";
import { createProjectActivity } from "../../../api/project_activity";
import { fetchProject } from "../../../api/project";
import { TimeRegistrationSection } from "../../../components/dashboard/hours/time-registration-section";
import { WorkOrderChecklistPanel } from "../../../components/dashboard/workOrders/work-order-checklist-panel";

/** Forslag til AO-tittel fra avvik (nummer + tittel) når ordre opprettes fra avvik. */
function formatSuggestedWorkOrderTitleFromDeviation(hint, t) {
  if (!hint || typeof hint !== "object") return "";
  const label = (hint.displayTitle || hint.title || "").trim();
  const num = hint.number;
  const numStr =
    num != null && String(num).trim() !== "" ? String(num).trim() : "";
  if (numStr && label) {
    return t("deviation.work_order.suggested_wo_title_full", {
      number: numStr,
      title: label,
    });
  }
  if (label) return label;
  if (numStr) {
    return t("deviation.work_order.suggested_wo_title_number_only", {
      number: numStr,
    });
  }
  return "";
}

/** Pakker ut en enkelt post fra API (støtter { data: {...} } og dobbelt innpakket). */
function pickJsonRecord(body) {
  if (body == null || typeof body !== "object" || Array.isArray(body)) return null;
  const d = body.data;
  if (d != null && typeof d === "object" && !Array.isArray(d)) {
    if (
      "data" in d &&
      d.data != null &&
      typeof d.data === "object" &&
      !Array.isArray(d.data)
    ) {
      return d.data;
    }
    return d;
  }
  return body;
}

function deviationLinkFailureMessage(linkRes, t) {
  if (linkRes?.isAxiosError) {
    const errData = linkRes.response?.data;
    const errs = errData?.errors;
    if (Array.isArray(errs) && errs.length) return String(errs[0]);
    if (typeof errs === "string" && errs.trim()) return errs.trim();
    if (errs && typeof errs === "object" && !Array.isArray(errs)) {
      const flat = Object.values(errs).flat();
      if (flat.length) return String(flat[0]);
    }
    if (typeof errData?.error === "string") return errData.error;
  }
  return t("deviation.work_order.link_failed_after_create");
}

/** Fallback: AO-tittel fra prosjekt (nummer · visningsnavn) når det ikke finnes avvik-hint. */
function suggestedWorkOrderTitleFromProject(project) {
  if (!project) return "";
  const label = (project.displayTitle || project.name || "").trim();
  const num = project.project_number;
  const numStr = num != null && String(num).trim() !== "" ? String(num).trim() : "";
  if (label && numStr) {
    return `${numStr} · ${label}`;
  }
  return label || numStr;
}

/** Må ligge utenfor EditWorkOrder — ellers blir Formik remountet ved hver parent state-oppdatering (f.eks. kopier tittel). */
function EditWorkOrderFormContent({
  initialValues,
  setInitialValues,
  errors,
  setErrors,
  work_order,
  setWorkOrder,
  project,
  setProject,
  customer,
  setCustomer,
  delivery_address,
  setDeliveryAddress,
  canMutateWorkOrderFields,
  canEditWorkOrderUi,
  workOrderActivityListParams,
  createOrUpdate,
  navigate,
  t,
  returnAfterWorkOrderCreate = null,
  handleParticipantsChange,
  handleEventsMutated,
  costEstimationOpen,
  setCostEstimationOpen,
  newProjectActivityTitle,
  setNewProjectActivityTitle,
  creatingProjectActivity,
  setCreatingProjectActivity,
  openRegistrationDialogSignal,
  permissions,
}) {
  const qualityChecklistsModule = useCompanyHasModule("quality_checklists");
  const dispatch = useDispatch();
  const hoursController = useMemo(
    () => ({
      query: "",
      page: 1,
      per_page: 100,
      sortBy: "created_at",
      sort: "desc",
      filters:
        work_order?.id ? [{ name: "WORK_ORDER", value: work_order.id }] : [],
      view: null,
      fields: "time,effective_hourly_rate",
    }),
    [work_order?.id]
  );

  return (
    <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 pb-24 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3 lg:pb-0">
      <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
        <div className="divide-y divide-gray-200 overflow-hidden rounded-lg sm:gap-px sm:divide-y-0">
          <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            validationSchema={Yup.object({
              description: Yup.string(),
            })}
            onSubmit={async (values, helpers) => {
              try {
                const { submit, project_activity, ...rest } = values;
                const payload = {
                  ...rest,
                  project_activity_id:
                    rest.project_activity_id === "" ||
                    rest.project_activity_id == null
                      ? null
                      : Number(rest.project_activity_id) ||
                        rest.project_activity_id,
                };
                const response = await createOrUpdate(payload);
                console.log("Work order response:", response);

                const workOrderData = pickJsonRecord(response?.data ?? response);

                if (workOrderData?.id) {
                  setWorkOrder(workOrderData);
                  setInitialValues((prev) => ({
                    ...prev,
                    ...values,
                    id: workOrderData.id,
                    project_activity_id:
                      workOrderData.project_activity_id ??
                      payload.project_activity_id,
                    project_activity:
                      workOrderData.project_activity ??
                      values.project_activity,
                  }));
                  helpers.setStatus({ success: true });
                  helpers.setSubmitting(false);

                  const isFirstSave = !work_order?.id;
                  const deviationReturnId =
                    returnAfterWorkOrderCreate?.deviation_id;

                  if (isFirstSave && deviationReturnId) {
                    const linkRes = await updateDeviation({
                      id: deviationReturnId,
                      work_order_id: workOrderData.id,
                    });

                    const linkedDeviation = linkRes?.isAxiosError
                      ? null
                      : pickJsonRecord(linkRes) ?? linkRes;

                    if (linkedDeviation?.id) {
                      toast.success(
                        t("deviation.work_order.linked_after_create")
                      );
                      navigate(
                        `/dashboard/hse-ks/deviations/${deviationReturnId}`
                      );
                      return;
                    }

                    toast.error(deviationLinkFailureMessage(linkRes, t));
                    navigate(`/dashboard/work_orders/${workOrderData.id}`);
                    return;
                  }

                  toast.success("Arbeidsordre ble lagret");
                  navigate(`/dashboard/work_orders/${workOrderData.id}`);
                } else {
                  console.error(
                    "No work order data received:",
                    workOrderData
                  );
                  toast.error("No work order data received");
                  helpers.setSubmitting(false);
                }
              } catch (error) {
                console.error("Submission error:", error);
                const errorMessage =
                  error?.response?.data?.errors?.[0] ||
                  error?.message ||
                  "An error occurred while saving.";
                toast.error(errorMessage);
                setErrors([errorMessage]);
                helpers.setErrors({
                  submit: errorMessage,
                });
                helpers.setSubmitting(false);
              }
            }}
          >
            {(formik) => (
              <Form>
                {errors &&
                  errors.length > 0 &&
                  errors.map((error, index) => (
                    <PMNotice
                      severity={"error"}
                      key={index}
                      title="Error!"
                      description={error}
                    />
                  ))}

                <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
                  <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                    <div className="grid sm:grid-cols-8 gap-6">
                      <div className="sm:col-span-8">
                        <PMField
                          name="title"
                          type="text"
                          label={t("Title")}
                          disabled={!canMutateWorkOrderFields}
                          {...formik}
                        />
                      </div>
                      <div className="sm:col-span-4">
                        <PMField
                          name="order_date"
                          type="date"
                          label={t("Order date")}
                          disabled={!canMutateWorkOrderFields}
                          {...formik}
                        />
                      </div>
                      <div className="sm:col-span-4">
                        <PMField
                          name="number"
                          type="text"
                          label={t("Number")}
                          disabled={true}
                          {...formik}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="shadow sm:overflow-hidden sm:rounded-md mt-5">
                  <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                    <div className="grid sm:grid-cols-8 gap-6">
                      <div className="sm:col-span-2">
                        <ProjectSelector
                          name="project_id"
                          selected={project}
                          activeOnly="true"
                          disabled={!canMutateWorkOrderFields}
                          onChange={(e) => {
                            setProject(e?.target?.object);
                            setCustomer(e?.target?.object?.customer);
                            setDeliveryAddress(
                              e?.target?.object?.delivery_address
                            );
                            formik.setFieldValue("project_activity_id", null);
                            formik.setFieldValue("project_activity", null);
                          }}
                          {...formik}
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <CustomerSelector
                          label="Kunde"
                          selected={customer}
                          {...formik}
                        />
                      </div>

                      <div className="sm:col-span-4">
                        <PMField
                          name="due_date"
                          type="date"
                          label={t("Due date")}
                          disabled={!canMutateWorkOrderFields}
                          {...formik}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <div className="py-6">
                          <PMSwitch
                            name={"is_urgent"}
                            label={t("Is Urgent")}
                            disabled={!canMutateWorkOrderFields}
                            {...formik}
                          />
                        </div>
                      </div>

                      <div className="sm:col-span-3">
                        <DeliveryAddressSelector
                          label="Oppdragsadresse"
                          module="CUSTOMER"
                          module_param={customer?.id}
                          selected={delivery_address}
                          {...formik}
                        />
                      </div>

                      <div className="sm:col-span-8 border-t border-gray-100 pt-4 mt-2">
                        <h4 className="text-sm font-semibold text-gray-900">
                          {t("work_orders.project_activity.section_title")}
                        </h4>
                        <p className="mt-1 text-xs text-gray-500">
                          {t("work_orders.project_activity.help")}
                        </p>
                        <div className="mt-3 sm:max-w-xl">
                          <ModernProjectActivitySelectorField
                            name="project_activity_id"
                            label={false}
                            selected={formik.values.project_activity}
                            project={project}
                            setFieldValue={formik.setFieldValue}
                            errors={formik.errors}
                            disabled={!canMutateWorkOrderFields}
                            listFetchParams={workOrderActivityListParams}
                          />
                          {formik.values.project_activity_id != null &&
                            formik.values.project_activity_id !== "" &&
                            canMutateWorkOrderFields && (
                              <button
                                type="button"
                                className="mt-2 text-sm font-medium text-indigo-600 hover:text-indigo-500"
                                onClick={() => {
                                  formik.setFieldValue(
                                    "project_activity_id",
                                    null
                                  );
                                  formik.setFieldValue(
                                    "project_activity",
                                    null
                                  );
                                }}
                              >
                                {t("project_activity.clear_selection")}
                              </button>
                            )}
                        </div>
                        {canMutateWorkOrderFields && project?.id && (
                          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:max-w-2xl">
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                                <label
                                  htmlFor="new_project_activity_title"
                                  className="block text-sm font-medium text-gray-700"
                                >
                                  {t(
                                    "work_orders.project_activity.new_title_label"
                                  )}
                                </label>
                                <button
                                  type="button"
                                  disabled={
                                    creatingProjectActivity ||
                                    !String(
                                      formik.values.title || ""
                                    ).trim()
                                  }
                                  onClick={() =>
                                    setNewProjectActivityTitle(
                                      String(
                                        formik.values.title || ""
                                      ).trim()
                                    )
                                  }
                                  className="text-sm font-medium text-indigo-600 hover:text-indigo-500 disabled:cursor-not-allowed disabled:text-gray-400"
                                >
                                  {t(
                                    "work_orders.project_activity.copy_from_title"
                                  )}
                                </button>
                              </div>
                              <input
                                id="new_project_activity_title"
                                type="text"
                                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                value={newProjectActivityTitle}
                                onChange={(e) =>
                                  setNewProjectActivityTitle(e.target.value)
                                }
                                placeholder={t(
                                  "work_orders.project_activity.new_title_placeholder"
                                )}
                                disabled={creatingProjectActivity}
                              />
                            </div>
                            <PMButton
                              type="button"
                              className="shrink-0 w-full sm:w-auto"
                              disabled={
                                creatingProjectActivity ||
                                !newProjectActivityTitle.trim()
                              }
                              onClick={async () => {
                                const title = newProjectActivityTitle.trim();
                                if (!title || !project?.id) return;
                                setCreatingProjectActivity(true);
                                try {
                                  const row = await createProjectActivity(
                                    project.id,
                                    { title }
                                  );
                                  if (!row?.id) {
                                    throw new Error("Invalid response");
                                  }
                                  formik.setFieldValue(
                                    "project_activity_id",
                                    row.id
                                  );
                                  formik.setFieldValue(
                                    "project_activity",
                                    row
                                  );
                                  setNewProjectActivityTitle("");
                                  toast.success(
                                    t(
                                      "work_orders.project_activity.created_toast"
                                    )
                                  );
                                } catch (err) {
                                  const msg =
                                    err?.response?.data?.errors?.[0] ||
                                    err?.message ||
                                    t(
                                      "work_orders.project_activity.create_error"
                                    );
                                  toast.error(msg);
                                } finally {
                                  setCreatingProjectActivity(false);
                                }
                              }}
                            >
                              {creatingProjectActivity
                                ? t(
                                    "work_orders.project_activity.creating"
                                  )
                                : t(
                                    "work_orders.project_activity.create_button"
                                  )}
                            </PMButton>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Budget Section */}
                <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md mb-5">
                  <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                    <div className="grid grid-cols-6 gap-6">
                      <div className="col-span-6">
                        <h3 className="text-lg font-medium leading-6 text-gray-900 mb-2">
                          Budsjett
                        </h3>
                      </div>
                      <div className="sm:col-span-2">
                        <PMField
                          name="budgeted_hours"
                          type="number"
                          label={t("Budgeted Hours")}
                          step="0.5"
                          min="0"
                          disabled={!canMutateWorkOrderFields}
                          {...formik}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <PMSwitch
                          name="cost_included_in_project"
                          label={t("Cost included in project")}
                          disabled={!canMutateWorkOrderFields}
                          {...formik}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <PMSwitch
                          name="sales_income_included_in_project"
                          label={t("Sales income included in project")}
                          disabled={!canMutateWorkOrderFields}
                          {...formik}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <PMSwitch
                          name="do_not_expect_income"
                          label={t("Do not expect income")}
                          disabled={!canMutateWorkOrderFields}
                          {...formik}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md mb-5">
                  <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                    <div className="grid grid-cols-6 gap-6">
                      <div className="col-span-6 sm:col-span-3">
                        Beskrivelse
                      </div>

                      <div className="col-span-6">
                        {canMutateWorkOrderFields ? (
                          <QuillEditor
                            name="description"
                            rows={20}
                            {...formik}
                          />
                        ) : work_order?.description ? (
                          <HtmlTextFormatter
                            string={work_order?.description}
                          />
                        ) : (
                          <PMNotice
                            severity={"info"}
                            title="Ingen beskrivelse"
                            description="Ingen beskrivelse er lagt til."
                          />
                        )}
                      </div>

                      <div className="col-span-6 sm:col-span-3 lg:col-span-2"></div>
                    </div>
                  </div>
                </div>

                {/* Cost Estimation Section */}
                <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
                  <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() =>
                        setCostEstimationOpen(!costEstimationOpen)
                      }
                    >
                      <div>
                        <h3 className="text-lg font-medium leading-6 text-gray-900">
                          Kostnadsestimering
                        </h3>
                        <p className="mt-1 text-sm text-gray-500">
                          Estimert kostnad basert på timer og vareforbruk
                        </p>
                      </div>
                      <div className="flex items-center">
                        {costEstimationOpen ? (
                          <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                        ) : (
                          <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                    </div>

                    {costEstimationOpen && (
                      <div className="grid grid-cols-6 gap-6">
                        <div className="col-span-6 sm:col-span-6">
                          <div className="space-y-4">
                            <div className="border rounded-lg p-4">
                              <h4 className="font-medium text-gray-900 mb-3">
                                Arbeidskostnader
                              </h4>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">
                                    Budsjetterte timer
                                  </label>
                                  <p className="text-lg font-semibold text-gray-900">
                                    {formik.values.budgeted_hours || 0} timer
                                  </p>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">
                                    Gjennomsnittlig timepris
                                  </label>
                                  <p className="text-lg font-semibold text-gray-900">
                                    kr 500/t
                                  </p>
                                </div>
                                <div className="col-span-2">
                                  <label className="block text-sm font-medium text-gray-700">
                                    Estimert arbeidskostnad
                                  </label>
                                  <p className="text-xl font-bold text-blue-600">
                                    kr{" "}
                                    {(
                                      (formik.values.budgeted_hours || 0) *
                                      500
                                    ).toLocaleString()}
                                  </p>
                                </div>
                              </div>
                            </div>

                            <div className="border rounded-lg p-4">
                              <h4 className="font-medium text-gray-900 mb-3">
                                Materialkostnader
                              </h4>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">
                                    Estimert materialkostnad
                                  </label>
                                  <p className="text-lg font-semibold text-gray-900">
                                    kr 0
                                  </p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    * Kan registreres via vareforbruk
                                  </p>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">
                                    Status
                                  </label>
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                    Estimert
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
                              <h4 className="font-bold text-gray-900 mb-3">
                                Total estimert kostnad
                              </h4>
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">
                                  Sum kostnader:
                                </span>
                                <span className="text-2xl font-bold text-blue-600">
                                  kr{" "}
                                  {(
                                    (formik.values.budgeted_hours || 0) * 500
                                  ).toLocaleString()}
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 mt-2">
                                * Dette er et estimat basert på budsjetterte
                                timer og forventet materialkostnad
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {work_order?.id ? (
                  <WorkOrderAccessesSection
                    workOrderId={work_order.id}
                    participants={work_order.participants || []}
                    onParticipantsChange={handleParticipantsChange}
                    canManage={canEditWorkOrderUi}
                    className="mt-5"
                  />
                ) : (
                  <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
                    <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                      <div>
                        <h3 className="text-lg font-medium leading-6 text-gray-900">
                          {t("work_order.participants.title")}
                        </h3>
                        <p className="mt-1 text-sm text-gray-500">
                          {t("work_order.participants.draft_hint")}
                        </p>
                      </div>

                      <div className="grid grid-cols-6 gap-6">
                        <div className="col-span-6 sm:col-span-6">
                          <ParticipantsSection
                            drag={false}
                            showAdmin={false}
                            roles={workOrderParticipantsRole}
                            {...formik}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {work_order?.id && (
                  <WorkOrderEventsSection
                    workOrder={work_order}
                    events={work_order?.events || []}
                    canEdit={canEditWorkOrderUi}
                    onEventsMutated={handleEventsMutated}
                  />
                )}

                {work_order?.id && (
                  <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md mb-5">
                    <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                      <div className="grid grid-cols-6 gap-6">
                        <div className="col-span-6">
                          <DocumentCenter
                            subtitle="Last opp bilder og dokumenter"
                            module="work_order_description"
                            moduleParam={work_order?.id}
                          />
                        </div>
                        <div className="col-span-6 sm:col-span-3 lg:col-span-2"></div>
                      </div>
                    </div>
                  </div>
                )}

                {work_order?.id && (
                  <div className="mt-5 lg:grid lg:grid-cols-2 lg:gap-6">
                    <TimeRegistrationSection
                      className="hidden lg:block"
                      module="work_order"
                      moduleId={work_order.id}
                      project={project ?? work_order?.project}
                      workOrder={{
                        ...work_order,
                        project_id:
                          formik.values.project_id ?? work_order.project_id,
                        project: project ?? work_order?.project,
                        project_activity_id:
                          formik.values.project_activity_id ??
                          work_order.project_activity_id,
                        project_activity:
                          formik.values.project_activity ??
                          work_order.project_activity,
                      }}
                      onRefresh={() => {
                        if (work_order?.id) {
                          const filters = [
                            { name: "WORK_ORDER", value: work_order.id },
                          ];
                          dispatch(
                            getHoursAsync({ ...hoursController, filters })
                          );
                        }
                      }}
                      title="Timeregistrering"
                      description="Registrer timer for denne arbeidsordren"
                      openRegistrationDialogSignal={
                        openRegistrationDialogSignal
                      }
                    />

                    <div
                      role="button"
                      tabIndex={0}
                      disabled={!permissions.canItemConsumption}
                      className={`hidden lg:block shadow sm:overflow-hidden sm:rounded-md cursor-pointer transition-colors duration-200 hover:bg-gray-50 ${
                        !permissions.canItemConsumption
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                      onClick={
                        permissions.canItemConsumption
                          ? () =>
                              navigate(
                                `/dashboard/work_orders/${work_order?.id}/item-consumption`
                              )
                          : undefined
                      }
                      onKeyDown={(e) => {
                        if (!permissions.canItemConsumption) return;
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          navigate(
                            `/dashboard/work_orders/${work_order?.id}/item-consumption`
                          );
                        }
                      }}
                    >
                      <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                        <div className="flex items-center gap-4">
                          <div className="flex-shrink-0">
                            <CubeIcon className="h-12 w-12 text-teal-600" />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg font-medium leading-6 text-gray-900">
                              Vareforbruk
                            </h3>
                            <div className="mt-2">
                              <p className="text-sm text-gray-500">
                                Registrer forbruk av materialer og varer
                              </p>
                              <div className="mt-2 flex items-center gap-2">
                                <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                                  <PlusIcon className="h-3 w-3 mr-1" />
                                  {work_order?.items?.length || 0} varer
                                  registrert
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex-shrink-0">
                            <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {qualityChecklistsModule && work_order?.id ? (
                  <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md mb-5">
                    <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                      <h3 className="text-lg font-medium leading-6 text-gray-900">
                        {t("work_orders.checklist.section_title")}
                      </h3>
                      <WorkOrderChecklistPanel
                        workOrder={work_order}
                        onWorkOrderUpdated={(patch) =>
                          setWorkOrder((w) =>
                            w ? { ...w, ...patch } : w
                          )
                        }
                        canEdit={canMutateWorkOrderFields}
                      />
                    </div>
                  </div>
                ) : null}

                <div className="mt-5" />

                {canMutateWorkOrderFields && (
                  <FormikSaveBar
                    {...formik}
                    dockClassName="bottom-0 z-50 pb-2 sm:pb-5 max-lg:bottom-[4.75rem]"
                  />
                )}
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
}

export const EditWorkOrder = (props) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const location = useLocation();

  const [isLoading, setIsLoading] = useState(true);
  const isMananger = useUserHasAccess({ role: "projects_work-orders", action: "manage" });
  // const isMananger = false;
  const currentUser = useSelector((state) => state.session.currentUser);
  const dateToday = new Date().toISOString().substring(0, 10);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [workOrderPrintDialogOpen, setWorkOrderPrintDialogOpen] =
    useState(false);
  const [timeRegistrationDialogSignal, setTimeRegistrationDialogSignal] =
    useState(0);

  const [customer, setCustomer] = useState(
    location?.state?.customer || props?.customer
  );
  const [project, setProject] = useState(
    location?.state?.project || props?.project
  );
  const [delivery_address, setDeliveryAddress] = useState(
    location?.state?.delivery_address || props?.delivery_address
  );

  const navigate = useNavigate(); // Assuming you have a navigate function from react-router-dom

  const returnAfterWorkOrderCreate =
    location.state?.return_after_work_order_create ?? null;

  const [work_order, setWorkOrder] = useState(null);
  const permissions = useWorkOrderPermissions(work_order);
  const newWoProjectHydratedRef = useRef(null);

  useEffect(() => {
    if (id) return;
    const pid = project?.id;
    if (!pid) return;
    if (newWoProjectHydratedRef.current === pid) return;
    newWoProjectHydratedRef.current = pid;

    let cancelled = false;
    (async () => {
      try {
        const res = await fetchProject(pid, {
          fields: "displayTitle,project_number,name,customer,delivery_address",
        });
        const p = res?.data?.data ?? res?.data;
        if (cancelled || !p?.id) return;
        setProject((prev) => ({ ...(prev || {}), ...p }));
        if (p.customer) setCustomer(p.customer);
        if (p.delivery_address) setDeliveryAddress(p.delivery_address);
      } catch (_e) {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, project?.id]);
  const workOrdersModuleAccess = useProjectWorkOrdersAccess(
    work_order?.project ?? project
  );
  const canEditWorkOrderInModule = workOrdersModuleAccess.canEdit;
  const canCreateWorkOrderInModule = workOrdersModuleAccess.canCreate;
  const canDeleteWorkOrderInModule = workOrdersModuleAccess.canDelete;
  const canEditWorkOrderUi =
    Boolean(work_order?.id) &&
    Boolean(permissions?.canEdit) &&
    canEditWorkOrderInModule;
  const canMutateWorkOrderFields =
    (!work_order?.id && canCreateWorkOrderInModule) || canEditWorkOrderUi;

  const handleParticipantsChange = useCallback((next) => {
    setWorkOrder((w) => (w ? { ...w, participants: next } : w));
    setInitialValues((prev) => ({ ...prev, participants: next }));
  }, []);

  const handleEventsMutated = useCallback(({ action, event, id }) => {
    setWorkOrder((w) => {
      if (!w) return w;
      const evs = w.events || [];
      if (action === "deleted") {
        return { ...w, events: evs.filter((e) => e.id !== id) };
      }
      if (action === "created") {
        return { ...w, events: [...evs, event] };
      }
      if (action === "updated" && event) {
        return {
          ...w,
          events: evs.map((e) => (e.id === event.id ? event : e)),
        };
      }
      return w;
    });
  }, []);

  const [initialValues, setInitialValues] = useState({
    id: null,
    title: "",
    number: "",
    order_date: dateToday,
    due_date: dateToday,
    project_id: project?.id,
    description: "",
    is_urgent: false,
    budgeted_hours: 0,
    cost_included_in_project: false,
    sales_income_included_in_project: false,
    do_not_expect_income: false,
    uses_checklist: false,
    participants: [],
    project_activity_id: null,
    project_activity: null,
    submit: null,
  });

  const [costEstimationOpen, setCostEstimationOpen] = useState(false);
  const [newProjectActivityTitle, setNewProjectActivityTitle] = useState("");
  const [creatingProjectActivity, setCreatingProjectActivity] = useState(false);

  const workOrderActivityListParams = useMemo(() => {
    const p = { selectable_for_work_order: true };
    if (work_order?.id) p.work_order_id = work_order.id;
    return p;
  }, [work_order?.id]);

  useEffect(() => {
    // if ID is set, fetch the work_order
    setTimeout(() => {
      if (id) {
        setIsLoading(true);
        getWorkOrder(id);
      } else {
        setIsLoading(false);
      }
    }, 500);
  }, [id]);

  // Update state when location.state changes
  useEffect(() => {
    if (location?.state) {
      setCustomer(location.state.customer || customer);
      setProject(location.state.project || project);
      setDeliveryAddress(location.state.delivery_address || delivery_address);
    }
  }, [location?.state]);

  useEffect(() => {
    if (work_order) {
      setInitialValues({
        id: work_order.id,
        title: work_order?.title,
        number: work_order?.number,
        order_date: work_order?.order_date || dateToday,
        due_date: work_order?.due_date || dateToday,
        project_id: work_order?.project_id || project?.id,
        customer_id: work_order?.customer_id || customer?.id,
        delivery_address_id:
          work_order?.delivery_address_id || delivery_address?.id,
        description: work_order?.description || "",
        budgeted_hours: work_order?.budgeted_hours || 0,
        cost_included_in_project: work_order?.cost_included_in_project || false,
        sales_income_included_in_project: work_order?.sales_income_included_in_project || false,
        do_not_expect_income: work_order?.do_not_expect_income || false,
        uses_checklist: work_order?.uses_checklist || false,
        participants: work_order?.participants || [],
        project_activity_id:
          work_order?.project_activity_id ??
          work_order?.project_activity?.id ??
          null,
        project_activity: work_order?.project_activity ?? null,
        submit: null,
      });
    } else {
      const paFromNav = location?.state?.project_activity;
      const paIdFromNav = location?.state?.project_activity_id;

      setInitialValues((prev) => {
        const deviationHint = location?.state?.work_order_title_from_deviation;
        const fromDeviation = formatSuggestedWorkOrderTitleFromDeviation(
          deviationHint,
          t
        );
        const suggestedTitle =
          fromDeviation ||
          suggestedWorkOrderTitleFromProject(project) ||
          "";
        const nextCustomerId =
          customer?.id ?? project?.customer?.id ?? prev.customer_id ?? null;
        const nextDeliveryId =
          delivery_address?.id ??
          project?.delivery_address?.id ??
          prev.delivery_address_id ??
          null;

        const titleKept =
          typeof prev.title === "string" && prev.title.trim() !== ""
            ? prev.title
            : suggestedTitle || "";

        return {
          id: null,
          title: titleKept,
          number: "",
          order_date: dateToday,
          due_date: dateToday,
          project_id: project?.id ?? prev.project_id,
          customer_id: nextCustomerId,
          delivery_address_id: nextDeliveryId,
          description: "",
          is_urgent: false,
          budgeted_hours: 0,
          cost_included_in_project: false,
          sales_income_included_in_project: false,
          do_not_expect_income: false,
          uses_checklist: false,
          participants: [],
          project_activity_id:
            paFromNav?.id ??
            (paIdFromNav != null && paIdFromNav !== ""
              ? Number(paIdFromNav) || paIdFromNav
              : null),
          project_activity: paFromNav ?? null,
          submit: null,
        };
      });
    }
  }, [
    work_order?.id,
    work_order?.updated_at,
    work_order?.title,
    work_order?.number,
    work_order?.order_date,
    work_order?.due_date,
    work_order?.project_id,
    work_order?.customer_id,
    work_order?.delivery_address_id,
    work_order?.description,
    work_order?.budgeted_hours,
    work_order?.cost_included_in_project,
    work_order?.sales_income_included_in_project,
    work_order?.do_not_expect_income,
    work_order?.uses_checklist,
    work_order?.project_activity_id,
    work_order?.project_activity,
    dateToday,
    project?.id,
    project?.displayTitle,
    project?.project_number,
    project?.name,
    project?.customer?.id,
    project?.delivery_address?.id,
    customer?.id,
    delivery_address?.id,
    location?.state?.project_activity,
    location?.state?.project_activity_id,
    location?.state?.work_order_title_from_deviation,
    t,
  ]);

  const getWorkOrder = (id) => {
    setIsLoading(true);
    try {
      fetchWorkOrder(id, { fields: "*" }).then((res) => {
        setIsLoading(false);
        const wo = res?.data?.data ?? res?.data;
        setWorkOrder(wo);
        setProject(wo?.project);
        setCustomer(wo?.customer);
        setDeliveryAddress(wo?.delivery_address);
      });
    } catch {
      toast.error("Something went wrong when trying to load the report");
      setIsLoading(false);
    }
  };

  const createOrUpdate = (data) => {
    if (work_order?.id) {
      return updateWorkOrder(data, { params: { fields: "*" } });
    } else {
      return createWorkOrder(data, { fields: "*" });
    }
  };

  // get userType form searchParams
  const searchParams = new URLSearchParams(location.search);
  const _userType = searchParams.get("userType") || "";

  const [errors, setErrors] = useState([]);

  // remove errors after 6 seconds
  useEffect(() => {
    if (errors && errors.length > 0) {
      setTimeout(() => {
        setErrors([]);
      }, 20000);
    }
  }, [errors]);

  const [approveDialogOpen, setApproveDialogOpen] = useState(false);

  const canApprove = () => {
    if (isMananger)
      if (!work_order?.accepted) {
        return true;
      }
    return false;
  };

  const headerActions = useMemo(
    () => [
      {
        component: null,
        label: t("work_orders.back_to_view"),
        description: "",
        path: null,
        action: () => {
          const wid = work_order?.id || id;
          navigate(
            wid ? `/dashboard/work_orders/${wid}` : "/dashboard/work_orders"
          );
        },
        icon: ArrowLeftIcon,
        textColor: "text-teal-700",
        bgColor: "bg-white",
        hidden: false,
        menu: false,
      },
      {
        component: null,
        label: "Skriv ut",
        description: "",
        path: null,
        action: () => setWorkOrderPrintDialogOpen(true),
        icon: PrinterIcon,
        textColor: "text-teal-700",
        bgColor: "bg-white",
        hidden: !work_order?.id,
        menu: false,
      },
      {
        component: null,
        label: "Slett",
        description: "",
        path: null,
        action: () => setDeleteConfirmationOpen(true),
        icon: TrashIcon,
        textColor: "text-teal-700",
        bgColor: "bg-white",
        hidden: !(canDeleteWorkOrderInModule || isMananger),
        menu: true,
      },
    ],
    [t, work_order?.id, id, navigate, isMananger, canDeleteWorkOrderInModule]
  );

  const headerMeta = [
    {
      label: "wd",
      component: <ItemStatus item={work_order} />,
      path: null,
      action: null,
      icon: CheckBadgeIcon,
      textColor: "text-teal-700",
      hidden: false,
    },
    {
      label: "Godkjent av " + work_order?.approved_by?.displayTitle,
      path: null,
      action: null,
      icon: CheckBadgeIcon,
      textColor: "text-teal-700",
      hidden: !work_order?.accepted,
    },
  ];

  const handleDeleteWorkOrder = () => {
    if (!work_order?.id) return;
    setDeleteConfirmationOpen(false);
    setIsLoading(true);
    deleteWorkOrder(work_order.id)
      .then(() => {
        setIsLoading(false);
        toast.success(t("work_orders.delete_success"));
        navigate("/dashboard/work_orders");
      })
      .catch((err) => {
        setIsLoading(false);
        const raw = err?.response?.data?.errors;
        const msg = Array.isArray(raw)
          ? raw[0]
          : typeof raw === "string"
            ? raw
            : null;
        toast.error(msg || t("work_orders.delete_failed"));
      });
  };




  return (
    <>
      <DashboardHeader
        subtitle=""
        meta={headerMeta}
        actions={headerActions}
        title={(!work_order?.id ? "Ny" : "Redigerer") + " Arbeidsordre"}
      >
        {isLoading ? (
          <ResourceLoading />
        ) : (
          <>
            <EditWorkOrderFormContent
              initialValues={initialValues}
              setInitialValues={setInitialValues}
              errors={errors}
              setErrors={setErrors}
              work_order={work_order}
              setWorkOrder={setWorkOrder}
              project={project}
              setProject={setProject}
              customer={customer}
              setCustomer={setCustomer}
              delivery_address={delivery_address}
              setDeliveryAddress={setDeliveryAddress}
              canMutateWorkOrderFields={canMutateWorkOrderFields}
              canEditWorkOrderUi={canEditWorkOrderUi}
              workOrderActivityListParams={workOrderActivityListParams}
              createOrUpdate={createOrUpdate}
              navigate={navigate}
              returnAfterWorkOrderCreate={returnAfterWorkOrderCreate}
              t={t}
              handleParticipantsChange={handleParticipantsChange}
              handleEventsMutated={handleEventsMutated}
              costEstimationOpen={costEstimationOpen}
              setCostEstimationOpen={setCostEstimationOpen}
              newProjectActivityTitle={newProjectActivityTitle}
              setNewProjectActivityTitle={setNewProjectActivityTitle}
              creatingProjectActivity={creatingProjectActivity}
              setCreatingProjectActivity={setCreatingProjectActivity}
              openRegistrationDialogSignal={timeRegistrationDialogSignal}
              permissions={permissions}
            />
            {work_order?.id && (
              <nav
                className="lg:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-gray-200 bg-white/95 shadow-[0_-4px_14px_rgba(0,0,0,0.06)] backdrop-blur-sm supports-[backdrop-filter]:bg-white/90"
                style={{
                  paddingBottom:
                    "max(0.35rem, env(safe-area-inset-bottom, 0px))",
                }}
                aria-label={t("work_order.view.mobileNav.ariaLabel")}
              >
                <div className="flex items-stretch justify-around gap-0 px-0.5 pt-1">
                  <button
                    type="button"
                    disabled={!permissions.canItemConsumption}
                    onClick={() =>
                      navigate(
                        `/dashboard/work_orders/${work_order.id}/item-consumption`
                      )
                    }
                    className="flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-lg px-0.5 py-1.5 text-gray-600 transition-colors hover:bg-gray-50 active:bg-gray-100 disabled:pointer-events-none disabled:opacity-40"
                  >
                    <CubeIcon className="h-5 w-5 shrink-0" aria-hidden />
                    <span className="max-w-full text-center text-[10px] font-medium leading-tight">
                      {t("report.view.mobileNav.items")}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setTimeRegistrationDialogSignal((n) => n + 1)
                    }
                    className="flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-lg px-0.5 py-1.5 text-gray-600 transition-colors hover:bg-gray-50 active:bg-gray-100"
                  >
                    <ClockIcon className="h-5 w-5 shrink-0" aria-hidden />
                    <span className="max-w-full text-center text-[10px] font-medium leading-tight">
                      {t("report.view.mobileNav.timeRegistration")}
                    </span>
                  </button>
                </div>
              </nav>
            )}
          </>
        )}
      </DashboardHeader>

      <ProConfirmation
        severity="confirmation"
        open={deleteConfirmationOpen}
        message={t("work_orders.delete_confirm")}
        action="delete"
        module="work_order"
        yesCallback={handleDeleteWorkOrder}
        cancelCallback={() => setDeleteConfirmationOpen(false)}
      />

      <WorkOrderPrintDialog
        open={workOrderPrintDialogOpen}
        onClose={() => setWorkOrderPrintDialogOpen(false)}
        workOrder={work_order}
        onRefresh={(data) => {
          if (data) {
            setWorkOrder(data);
          }
        }}
      />
    </>
  );
};
